import { Reducer } from 'redux'
import { SmartGtmActionTypes } from './action'
import { AccountPipelineHealth, PropensityRow } from '../Propensity/reducer'

export type HealthLevel = 'high' | 'medium' | 'low'

export interface HealthLevelConfig {
  level: HealthLevel
  label: string
  colorClass: string
  getPercentage: (health: AccountPipelineHealth) => number
}

export type ActionItem = {
  id: string
  sellerId: string
  opportunityId: string
  actionText: string
  actionCategory: string
  urgencyScore: number
  contextIconType: string
  callToActionLabel: string
  prompt: string
  status: 'new' | 'complete' | 'failed' | 'in_progress'
  isHidden: boolean
  performedAt: string | null
  performedByUserId: string | null
  displayOrder: number
  createdAt: string
  reason?: string | null
  updatedAt: string
}

export type Risk = {
  id: string
  opportunityId: string
  reason?: string
  risk?: string
  riskScore?: number
}
export type NoteHistoryItem = {
  userId: string
  note: string
  updatedAt: string
}

export type OpportunityNote = {
  id: string
  opportunityId: string
  userId: string
  notes: string
  history: NoteHistoryItem[]
  createdAt: string
  updatedAt: string
  lockOwnerId: string | null
  lockExpiresAt: string | null
}

export type LockNotesPayload = {
  userId: string
  lockDurationSeconds?: number
}

export type FetchNotesOptions = {
  withLoader?: boolean
}

export interface SmartGtmData {
  criticalActions: ActionItem[] | []
  selectedActionId: string | null
  selectedRow: PropensityRow | null
  openAiDrawer: boolean
  risks: Risk[] | []
  lastPrompt: string | null
  awsUserId: string | null
  hubspotpartnerId: string | null
  opportunityNotes: OpportunityNote | null
}

type actionType =
  | {
      type: SmartGtmActionTypes.SET_CRITICAL_ACTIONS
      payload: ActionItem[]
    }
  | {
      type: SmartGtmActionTypes.CLEAR_CRITICAL_ACTIONS
    }
  | {
      type: SmartGtmActionTypes.UPDATE_CRITICAL_ACTION
      payload: { id: string; updates: Partial<ActionItem> }
    }
  | {
      type: SmartGtmActionTypes.SET_SELECTED_ACTION_ID
      payload: string | null
    }
  | {
      type: SmartGtmActionTypes.CLEAR_SELECTED_ACTION_ID
    }
  | {
      type: SmartGtmActionTypes.SET_OPEN_AI_DRAWER
      payload: boolean
    }
  | {
      type: SmartGtmActionTypes.CLEAR_OPEN_AI_DRAWER
    }
  | {
      type: SmartGtmActionTypes.SET_SELECTED_ROW
      payload: PropensityRow | null
    }
  | {
      type: SmartGtmActionTypes.CLEAR_SELECTED_ROW
    }
  | {
      type: SmartGtmActionTypes.SET_OPPORTUNITY_NOTES
      payload: OpportunityNote
    }
  | {
      type: SmartGtmActionTypes.UPDATE_OPPORTUNITY_NOTES
      payload: Partial<OpportunityNote>
    }
  | {
      type: SmartGtmActionTypes.CLEAR_OPPORTUNITY_NOTES
    }
  | {
      type: SmartGtmActionTypes.SET_RISKS
      payload: Risk[]
    }
  | {
      type: SmartGtmActionTypes.CLEAR_RISKS
    }
  | {
      type: SmartGtmActionTypes.SET_LAST_PROMPT
      payload: string
    }
  | {
      type: SmartGtmActionTypes.CLEAR_LAST_PROMPT
    }
  | {
      type: SmartGtmActionTypes.SET_AWS_USER_ID
      payload: string
    }
  | {
      type: SmartGtmActionTypes.CLEAR_AWS_USER_ID
    }
  | {
      type: SmartGtmActionTypes.SET_HUBSPOT_PARTNER_ID
      payload: string
    }
  | {
      type: SmartGtmActionTypes.CLEAR_HUBSPOT_PARTNER_ID
    }
const emptyOpportunityNote: OpportunityNote = {
  id: '',
  opportunityId: '',
  userId: '',
  notes: '',
  history: [],
  createdAt: '',
  updatedAt: '',
  lockOwnerId: null,
  lockExpiresAt: null,
}

export const initialState: SmartGtmData = {
  criticalActions: [],
  selectedActionId: null,
  openAiDrawer: false,
  selectedRow: null,
  opportunityNotes: null,
  risks: [],
  lastPrompt: null,
  awsUserId: null,
  hubspotpartnerId: null,
}

export const reducer: Reducer<SmartGtmData, actionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case SmartGtmActionTypes.SET_CRITICAL_ACTIONS: {
      return {
        ...state,
        criticalActions: action.payload,
      }
    }
    case SmartGtmActionTypes.CLEAR_CRITICAL_ACTIONS: {
      return {
        ...state,
        criticalActions: [],
      }
    }
    case SmartGtmActionTypes.UPDATE_CRITICAL_ACTION: {
      return {
        ...state,
        criticalActions: state.criticalActions.map(criticalAction =>
          criticalAction.id === action.payload.id
            ? { ...criticalAction, ...action.payload.updates }
            : criticalAction
        ),
      }
    }
    case SmartGtmActionTypes.SET_SELECTED_ACTION_ID: {
      return {
        ...state,
        selectedActionId: action.payload,
      }
    }
    case SmartGtmActionTypes.CLEAR_SELECTED_ACTION_ID: {
      return {
        ...state,
        selectedActionId: null,
      }
    }
    case SmartGtmActionTypes.SET_OPEN_AI_DRAWER: {
      return {
        ...state,
        openAiDrawer: action.payload,
      }
    }
    case SmartGtmActionTypes.CLEAR_OPEN_AI_DRAWER: {
      return {
        ...state,
        openAiDrawer: false,
      }
    }
    case SmartGtmActionTypes.SET_SELECTED_ROW: {
      return {
        ...state,
        selectedRow: action.payload,
      }
    }
    case SmartGtmActionTypes.CLEAR_SELECTED_ROW: {
      return {
        ...state,
        selectedRow: null,
      }
    }
    case SmartGtmActionTypes.SET_OPPORTUNITY_NOTES: {
      return {
        ...state,
        opportunityNotes: action.payload,
      }
    }
    case SmartGtmActionTypes.UPDATE_OPPORTUNITY_NOTES: {
      return {
        ...state,
        opportunityNotes: state.opportunityNotes
          ? { ...state.opportunityNotes, ...action.payload }
          : { ...emptyOpportunityNote, ...action.payload },
      }
    }
    case SmartGtmActionTypes.CLEAR_OPPORTUNITY_NOTES: {
      return {
        ...state,
        opportunityNotes: null,
      }
    }
    case SmartGtmActionTypes.SET_RISKS: {
      return {
        ...state,
        risks: action.payload,
      }
    }
    case SmartGtmActionTypes.CLEAR_RISKS: {
      return {
        ...state,
        risks: [],
      }
    }
    case SmartGtmActionTypes.SET_LAST_PROMPT: {
      return {
        ...state,
        lastPrompt: action.payload,
      }
    }
    case SmartGtmActionTypes.CLEAR_LAST_PROMPT: {
      return {
        ...state,
        lastPrompt: null,
      }
    }
    case SmartGtmActionTypes.SET_AWS_USER_ID: {
      return {
        ...state,
        awsUserId: action.payload,
      }
    }
    case SmartGtmActionTypes.CLEAR_AWS_USER_ID: {
      return {
        ...state,
        awsUserId: null,
      }
    }
    case SmartGtmActionTypes.SET_HUBSPOT_PARTNER_ID: {
      return {
        ...state,
        hubspotpartnerId: action.payload,
      }
    }
    case SmartGtmActionTypes.CLEAR_HUBSPOT_PARTNER_ID: {
      return {
        ...state,
        hubspotpartnerId: null,
      }
    }
    default: {
      return state
    }
  }
}
